/**
 * Rudrik Account Takeover (ATO) Detection
 * ========================================
 * Per-event detector for login / password change / OTP / beneficiary add /
 * large TX. Surfaces impossible-travel, SIM-swap, malicious IP, etc.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  KeyRound, Plane, Smartphone, ShieldOff, AlertTriangle, Loader2, RefreshCw,
  FlaskConical, Wifi, MapPin, Mail, Phone, ShieldCheck, Lock, MessageSquare,
  Link2,
} from "lucide-react";
import { toast } from "sonner";
import { getFingerprintId, awaitFingerprintId } from "../lib/fingerprint";

const API = `${window.location.origin}/api/ato`;
const RISK = { LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#f97316", CRITICAL: "#dc2626" };
const ACTION = { ALLOW: "#22c55e", STEP_UP_AUTH: "#f59e0b", BLOCK_SESSION: "#f97316", LOCK_ACCOUNT: "#dc2626" };
const SEV = { CRITICAL: "#dc2626", HIGH: "#f97316", MEDIUM: "#f59e0b", LOW: "#22c55e" };

function F({ label, value, onChange, type = "text", testId }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <input type={type} value={value} data-testid={testId}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value || 0) : e.target.value)}
        style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 10px", color: "#f1f5f9", fontSize: 12 }} />
    </label>
  );
}

function S({ label, value, onChange, options, testId }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 10px", color: "#f1f5f9", fontSize: 12 }}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function T({ label, value, onChange, testId }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, cursor: "pointer" }}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 600 }}>{label}</span>
    </label>
  );
}

export default function AtoPage() {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [eventType, setEventType] = useState("LOGIN");
  const [customerId, setCustomerId] = useState("CUST-001");
  const [newDevice, setNewDevice] = useState(false);
  const [newLocation, setNewLocation] = useState(false);
  const [vpn, setVpn] = useState(false);
  const [failed, setFailed] = useState(0);
  const [simSwap, setSimSwap] = useState(false);
  const [simDays, setSimDays] = useState(999);
  const [emailChg, setEmailChg] = useState(false);
  const [phoneChg, setPhoneChg] = useState(false);
  const [ipScore, setIpScore] = useState(5);
  const [lastCity, setLastCity] = useState("Bengaluru");
  const [curCity, setCurCity] = useState("Bengaluru");
  const [hoursSince, setHoursSince] = useState(12);
  const [amount, setAmount] = useState(0);
  const [newBen, setNewBen] = useState(false);
  const [ipAddr, setIpAddr] = useState("");
  const [emailAddr, setEmailAddr] = useState("");

  useEffect(() => {
    axios.get(`${API}/sample-scenarios`, { withCredentials: true })
      .then(({ data }) => setScenarios(data.scenarios || []))
      .catch(() => {});
  }, []);

  const applyScenario = (s) => {
    const p = s.payload;
    setEventType(p.event_type || "LOGIN"); setCustomerId(p.customer_id || "");
    setNewDevice(!!p.new_device); setNewLocation(!!p.new_location);
    setVpn(!!p.vpn_detected); setFailed(p.failed_attempts_before || 0);
    setSimSwap(!!p.sim_swap_recent); setSimDays(p.sim_swap_days_ago ?? 999);
    setEmailChg(!!p.email_recently_changed); setPhoneChg(!!p.phone_recently_changed);
    setIpScore(p.ip_score || 0);
    setLastCity(p.last_login_city || ""); setCurCity(p.current_login_city || "");
    setHoursSince(p.hours_since_last_login ?? 12);
    setAmount(p.amount_inr || 0); setNewBen(!!p.new_beneficiary_same_day);
    setResult(null);
    toast.success(`Loaded: ${s.label}`);
  };

  const run = async () => {
    setLoading(true); setResult(null);
    const fingerprintId = await awaitFingerprintId(800);
    try {
      const payload = {
        event_type: eventType, customer_id: customerId,
        new_device: newDevice, new_location: newLocation, vpn_detected: vpn,
        failed_attempts_before: Number(failed) || 0,
        sim_swap_recent: simSwap, sim_swap_days_ago: Number(simDays) || 999,
        email_recently_changed: emailChg, phone_recently_changed: phoneChg,
        ip_score: Number(ipScore) || 0,
        last_login_city: lastCity, current_login_city: curCity,
        hours_since_last_login: Number(hoursSince) || 0,
        amount_inr: Number(amount) || 0,
        new_beneficiary_same_day: newBen,
        ip_address: ipAddr || undefined,
        email: emailAddr || undefined,
        fingerprint_id: fingerprintId,
      };
      const { data } = await axios.post(`${API}/score`, payload, { withCredentials: true, timeout: 25000 });
      setResult(data.result);
      const r = data.result;
      if (r.risk_level === "CRITICAL") toast.error(`${r.recommended_action} — CRITICAL · score ${r.takeover_score}`);
      else if (r.risk_level === "HIGH") toast.warning(`${r.recommended_action} — HIGH · score ${r.takeover_score}`);
      else if (r.risk_level === "MEDIUM") toast.warning(`${r.recommended_action} — MEDIUM`);
      else toast.success(`${r.recommended_action} — LOW`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ATO scoring failed");
    } finally { setLoading(false); }
  };

  const riskColor = RISK[result?.risk_level] || "#94a3b8";
  const actionColor = ACTION[result?.recommended_action] || "#94a3b8";

  return (
    <div data-testid="ato-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-1">
        <ShieldOff style={{ width: 26, height: 26, color: "#f97316" }} />
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Account Takeover Detection</h1>
        <span style={{ background: "rgba(249,115,22,0.10)", color: "#fdba74", border: "1px solid #f9731655", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Session-only</span>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
        Per-event ATO scoring — impossible travel, SIM-swap, malicious IP, VPN-tunnelled password change, and more.
        Returns step-up-auth or lock-account in &lt; 1s.
      </p>

      {scenarios.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          <span style={{ color: "#64748b", fontSize: 11, alignSelf: "center", marginRight: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <FlaskConical size={11} /> Quick demos:
          </span>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => applyScenario(s)} data-testid={`sample-${s.id}`}
              style={{ background: "#0d1117", border: "1px solid #1e2530", color: "#cbd5e1", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <KeyRound size={11} style={{ color: "#f97316" }} /> Event
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <S label="Event type" value={eventType} onChange={setEventType}
               options={["LOGIN", "PASSWORD_CHANGE", "OTP_REQUEST", "BENEFICIARY_ADD", "LARGE_TX"]} testId="f-event" />
            <F label="Customer ID" value={customerId} onChange={setCustomerId} testId="f-cust" />
            <F label="Amount (INR, if TX)" value={amount} onChange={setAmount} type="number" testId="f-amount" />
            <F label="Failed OTP before success" value={failed} onChange={setFailed} type="number" testId="f-failed" />
          </div>
        </section>

        <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <MapPin size={11} style={{ color: "#f97316" }} /> Location + Device
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <F label="Last login city" value={lastCity} onChange={setLastCity} testId="f-lastcity" />
            <F label="Current login city" value={curCity} onChange={setCurCity} testId="f-curcity" />
            <F label="Hours since last login" value={hoursSince} onChange={setHoursSince} type="number" testId="f-hours" />
            <F label="IP malicious score (0-100)" value={ipScore} onChange={setIpScore} type="number" testId="f-ipscore" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <T label="New device" value={newDevice} onChange={setNewDevice} testId="f-newdev" />
              <T label="New location" value={newLocation} onChange={setNewLocation} testId="f-newloc" />
              <T label="VPN" value={vpn} onChange={setVpn} testId="f-vpn" />
              <T label="New benef. today" value={newBen} onChange={setNewBen} testId="f-newben" />
            </div>
          </div>
        </section>

        <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Smartphone size={11} style={{ color: "#f97316" }} /> Identity Hygiene
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <T label="SIM swap recent (≤30d)" value={simSwap} onChange={setSimSwap} testId="f-simswap" />
            <F label="SIM swap days ago" value={simDays} onChange={setSimDays} type="number" testId="f-simdays" />
            <T label="Email recently changed" value={emailChg} onChange={setEmailChg} testId="f-emailchg" />
            <T label="Phone recently changed" value={phoneChg} onChange={setPhoneChg} testId="f-phonechg" />
            <F label="IP address (for IPQS/AbuseIPDB)" value={ipAddr} onChange={setIpAddr} testId="f-ip" />
            <F label="Email (for HIBP breach check)" value={emailAddr} onChange={setEmailAddr} testId="f-email" />
            <div style={{ fontSize: 9, color: "#64748b", marginTop: 2, lineHeight: 1.4 }}>
              Device fingerprint: <span style={{ color: "#a855f7", fontFamily: "monospace" }} data-testid="fp-preview">{getFingerprintId().slice(0, 18)}…</span>
            </div>
          </div>
        </section>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 16 }}>
        <button onClick={run} disabled={loading} data-testid="run-btn"
          style={{ flex: 1, background: loading ? "#1e2530" : "linear-gradient(135deg, #f97316, #c2410c)",
                   border: "none", color: "white", borderRadius: 6, padding: "11px 18px",
                   fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                   display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {loading ? <Loader2 className="animate-spin" size={14} /> : <ShieldOff size={14} />}
          {loading ? "Scoring…" : "Score event"}
        </button>
      </div>

      {result && (
        <>
          <div data-testid="ato-result" style={{
            background: "#0d1117", border: `1px solid ${actionColor}66`, borderRadius: 12, padding: 22, marginBottom: 12,
            display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 16, alignItems: "center",
          }}>
            <div>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Action</div>
              <div data-testid="action" style={{ color: actionColor, fontSize: 22, fontWeight: 800, marginTop: 4 }}>{result.recommended_action}</div>
              <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 6 }}>{result.summary}</div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 6 }}>
                {result.ai_mode} · {result.latency_ms}ms · event {result.event_type}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Takeover Score</div>
              <div data-testid="score" style={{ color: riskColor, fontSize: 38, fontWeight: 800 }}>{result.takeover_score}</div>
              <div data-testid="risk" style={{ color: riskColor, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>{result.risk_level}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Step-up Auth</div>
              <div data-testid="stepup" style={{ color: result.step_up_auth_required ? "#fca5a5" : "#22c55e", fontSize: 14, fontWeight: 800, marginTop: 6 }}>
                {result.step_up_auth_required ? result.step_up_type : "None"}
              </div>
              <div style={{ color: "#64748b", fontSize: 10 }}>{result.step_up_auth_required ? "Required" : "Not needed"}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Notify</div>
              <div data-testid="notify" style={{ color: result.notification_required ? "#fbbf24" : "#22c55e", fontSize: 14, fontWeight: 800, marginTop: 6 }}>
                {result.notification_required ? `${result.notify_channels.length} ch.` : "Silent"}
              </div>
              <div style={{ color: "#64748b", fontSize: 10 }}>{(result.notify_channels || []).join(" · ") || "—"}</div>
            </div>
          </div>

          {result.external_enrichment && (
            <section data-testid="enrichment" style={{
              background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10,
              padding: 14, marginBottom: 12,
            }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <Wifi size={11} style={{ color: "#22d3ee" }} /> External Signal Enrichment
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[
                  { key: "ipqs", label: "IPQualityScore", data: result.external_enrichment?.ipqs, configured: result.external_enrichment?.configured?.ipqs,
                    rows: (d) => [
                      ["Fraud score", `${d.fraud_score}/100`, d.fraud_score >= 75 ? "#dc2626" : d.fraud_score >= 40 ? "#f59e0b" : "#22c55e"],
                      ["VPN", d.is_vpn ? "YES" : "no", d.is_vpn ? "#dc2626" : "#22c55e"],
                      ["Proxy/Tor", (d.is_proxy || d.is_tor) ? "YES" : "no", (d.is_proxy || d.is_tor) ? "#dc2626" : "#22c55e"],
                      ["Country/ISP", `${d.country_code || "?"} · ${(d.isp || "").slice(0, 18)}`, "#cbd5e1"],
                    ] },
                  { key: "abuseipdb", label: "AbuseIPDB", data: result.external_enrichment?.abuseipdb, configured: result.external_enrichment?.configured?.abuseipdb,
                    rows: (d) => [
                      ["Abuse score", `${d.abuse_confidence_score}/100`, d.abuse_confidence_score >= 50 ? "#dc2626" : "#22c55e"],
                      ["Reports", String(d.total_reports), "#cbd5e1"],
                      ["Usage type", d.usage_type || "?", "#cbd5e1"],
                    ] },
                  { key: "hibp", label: "HaveIBeenPwned", data: result.external_enrichment?.hibp, configured: result.external_enrichment?.configured?.hibp,
                    rows: (d) => [
                      ["Breaches found", String(d.length), d.length ? "#dc2626" : "#22c55e"],
                      d.length ? ["Latest", (d[0]?.title || d[0]?.name || "—").slice(0, 24), "#cbd5e1"] : null,
                      d.length ? ["Verified", d.some((b) => b.is_verified) ? "YES" : "no", "#cbd5e1"] : null,
                    ].filter(Boolean) },
                ].map((s) => {
                  const live = s.data !== null && s.data !== undefined;
                  return (
                    <div key={s.key} data-testid={`enrichment-${s.key}`} style={{
                      background: "#080c12", border: `1px solid ${live ? "#22c55e44" : "#1e2530"}`,
                      borderRadius: 6, padding: 10,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <span style={{ color: "#f1f5f9", fontSize: 11, fontWeight: 700 }}>{s.label}</span>
                        <span style={{ marginLeft: "auto", color: live ? "#22c55e" : (s.configured ? "#f59e0b" : "#64748b"),
                                       fontSize: 8, fontWeight: 800, padding: "2px 6px",
                                       background: live ? "rgba(34,197,94,0.10)" : "rgba(148,163,184,0.10)",
                                       border: `1px solid ${live ? "#22c55e55" : "#1e2530"}`, borderRadius: 4 }}>
                          {live ? "LIVE" : (s.configured ? "NO DATA" : "NOT CONFIGURED")}
                        </span>
                      </div>
                      {live ? (
                        <div style={{ display: "grid", gap: 4 }}>
                          {s.rows(s.data).map(([k, v, c], i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                              <span style={{ color: "#94a3b8" }}>{k}</span>
                              <span style={{ color: c, fontWeight: 700 }}>{v}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: "#64748b", fontSize: 10 }}>
                          {s.configured ? "Configured but no data returned. Engine falls back to heuristics." : `Add ${s.key.toUpperCase()}_API_KEY to backend/.env to enable live data.`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {result.impossible_travel?.detected && (
            <div style={{ background: "rgba(220,38,38,0.10)", border: "1px solid #dc262666", borderRadius: 10, padding: 14, marginBottom: 12,
                          display: "flex", alignItems: "center", gap: 12 }}>
              <Plane size={20} style={{ color: "#fca5a5" }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: "#fca5a5", fontSize: 13, fontWeight: 800 }}>Impossible travel detected</div>
                <div style={{ color: "#cbd5e1", fontSize: 11, marginTop: 4 }}>
                  {result.impossible_travel.last_login_city} → {result.impossible_travel.current_login_city} ·
                  {" "}{result.impossible_travel.distance_km} km in {result.impossible_travel.hours_between} h
                  {" "}(would require {result.impossible_travel.required_speed_kmh} km/h)
                </div>
              </div>
            </div>
          )}

          {result.fraud_chain && (
            <section data-testid="fraud-chain" style={{
              background: "#0d1117",
              border: `1px solid ${RISK[result.fraud_chain.fraud_risk] || "#1e2530"}77`,
              borderRadius: 10, padding: 14, marginBottom: 12,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Link2 size={13} style={{ color: "#a855f7" }} />
                <span style={{ color: "#a855f7", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Chained: Fraud Detection · LARGE_TX
                </span>
                <span style={{ marginLeft: "auto", color: RISK[result.fraud_chain.fraud_risk] || "#94a3b8", fontSize: 11, fontWeight: 800 }}>
                  {result.fraud_chain.fraud_risk} · {result.fraud_chain.fraud_score}/100 · {result.fraud_chain.fraud_action}
                </span>
              </div>
              <div style={{ color: "#cbd5e1", fontSize: 11, marginBottom: 8 }}>
                {result.fraud_chain.explanation}
              </div>
              {result.fraud_chain.rules_triggered?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {result.fraud_chain.rules_triggered.map((r, i) => {
                    const c = SEV[r.severity] || "#94a3b8";
                    return (
                      <span key={i} data-testid={`chain-rule-${r.id}`} style={{
                        background: `${c}15`, border: `1px solid ${c}55`, color: c,
                        borderRadius: 4, padding: "3px 7px", fontSize: 10, fontWeight: 700,
                      }}>
                        {r.id} · {r.name}
                      </span>
                    );
                  })}
                </div>
              )}
              {result.fraud_chain.str_required && (
                <div style={{ marginTop: 8, color: "#fca5a5", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={11} /> STR filing required — 7 days (FIU-IND)
                </div>
              )}
            </section>
          )}

          {result.risk_flags?.length > 0 && (
            <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <AlertTriangle size={11} style={{ color: "#f59e0b" }} /> Risk Flags ({result.risk_flags.length})
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {result.risk_flags.map((f, i) => {
                  const c = SEV[f.severity] || "#94a3b8";
                  return (
                    <div key={i} data-testid={`flag-${i}`} style={{ background: `${c}10`, border: `1px solid ${c}55`, borderRadius: 6, padding: "7px 11px", display: "flex", alignItems: "center", gap: 8 }}>
                      <AlertTriangle size={11} style={{ color: c }} />
                      <span style={{ color: c, fontSize: 12, fontWeight: 700 }}>{f.flag}</span>
                      <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{f.severity}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>Signal Snapshot</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
              {[
                ["New device", result.signals?.new_device],
                ["New location", result.signals?.new_location],
                ["VPN", result.signals?.vpn_detected],
                ["Impossible travel", result.signals?.impossible_travel],
                ["IP malicious", result.signals?.ip_malicious],
                ["SIM swap", result.signals?.sim_swap_recent],
                ["Email changed", result.signals?.email_recently_changed],
                ["Phone changed", result.signals?.phone_recently_changed],
              ].map(([label, v]) => {
                const c = v ? "#dc2626" : "#22c55e";
                return (
                  <div key={label} style={{ background: "#080c12", border: `1px solid ${c}55`, borderRadius: 6, padding: "5px 9px", display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#cbd5e1", fontSize: 10, fontWeight: 600 }}>{label}</span>
                    <span style={{ color: c, fontSize: 10, fontWeight: 800 }}>{v ? "YES" : "no"}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 8, color: "#64748b", fontSize: 11 }}>
              IP score {result.signals?.ip_score}/100 · Failed attempts {result.signals?.failed_attempts_before}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
